// TenantForm — modal that creates or edits a tenant.
import { useState, useEffect } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  employer: "",
  monthly_income: "",
  employment_type: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  emergency_contact_relationship: "",
  notes: "",
};

export default function TenantForm({ open, onOpenChange, propertyId, tenant, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!tenant;

  useEffect(() => {
    if (open) {
      setForm(
        tenant
          ? {
              name: tenant.name || "",
              email: tenant.email || "",
              phone: tenant.phone || "",
              employer: tenant.employer || "",
              monthly_income: tenant.monthly_income ?? "",
              employment_type: tenant.employment_type || "",
              emergency_contact_name: tenant.emergency_contact_name || "",
              emergency_contact_phone: tenant.emergency_contact_phone || "",
              emergency_contact_relationship: tenant.emergency_contact_relationship || "",
              notes: tenant.notes || "",
            }
          : EMPTY
      );
    }
  }, [open, tenant]);

  const handle = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email, and phone are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        employment_type: form.employment_type || null,
      };
      if (editing) {
        await api.patch(`/manage/tenants/${tenant.id}`, payload);
        toast.success("Tenant updated");
      } else {
        await api.post("/manage/tenants", { ...payload, property_id: propertyId });
        toast.success("Tenant added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save tenant");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-slate-200 bg-white" data-testid="tenant-form-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit tenant" : "Add tenant"}</DialogTitle>
          <DialogDescription className="text-slate-500">
            Required: name, email, phone. Other fields are optional but recommended for screening.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="t-name">Full name *</Label>
              <Input id="t-name" data-testid="tenant-form-name" value={form.name} onChange={(e) => handle("name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="t-email">Email *</Label>
              <Input id="t-email" type="email" data-testid="tenant-form-email" value={form.email} onChange={(e) => handle("email", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="t-phone">Phone *</Label>
              <Input id="t-phone" data-testid="tenant-form-phone" value={form.phone} onChange={(e) => handle("phone", e.target.value)} placeholder="+1 (305) 555-1234" />
            </div>
            <div>
              <Label htmlFor="t-employer">Employer</Label>
              <Input id="t-employer" value={form.employer} onChange={(e) => handle("employer", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="t-income">Monthly income</Label>
              <Input id="t-income" type="number" value={form.monthly_income} onChange={(e) => handle("monthly_income", e.target.value)} />
            </div>
            <div>
              <Label>Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => handle("employment_type", v)}>
                <SelectTrigger data-testid="tenant-form-employment"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {["W2","Self-employed","Contractor","Retired","Student","Other"].map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="ec-name">Emergency contact name</Label>
              <Input id="ec-name" value={form.emergency_contact_name} onChange={(e) => handle("emergency_contact_name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ec-phone">Emergency phone</Label>
              <Input id="ec-phone" value={form.emergency_contact_phone} onChange={(e) => handle("emergency_contact_phone", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ec-rel">Relationship</Label>
              <Input id="ec-rel" value={form.emergency_contact_relationship} onChange={(e) => handle("emergency_contact_relationship", e.target.value)} />
            </div>
          </div>

          <div>
            <Label htmlFor="t-notes">Notes</Label>
            <Textarea id="t-notes" rows={3} value={form.notes} onChange={(e) => handle("notes", e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700" disabled={saving} data-testid="tenant-form-submit">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editing ? "Save changes" : "Add tenant"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
